import { getQueryClient } from 'app/get-query-client';
import { REFERRALS_ALLOWLISTED_COUNTRY_CODES } from 'constants/restrictedCountries';
import { useAuthStore } from 'lib/stores/authStore';
import type { UserVo } from 'models';
import { referralsSummaryOptions } from 'queries/referralsSummary';
import { getWasReferred } from 'queries/referralsSummary.utils';
import { useSnackbarStore } from 'stores/zustand/snackbarStore';
import { getErrorMessage } from 'utils/errors';
import { noop } from 'utils/noop';
import { clearReferral, readDurableReferral } from 'utils/referralAttribution';
import { submitPostSignupReferralCode } from 'utils/referralCodeSubmission';

const LOGGED_IN_EMAIL_WAIT_TIMEOUT_MS = 15_000;
const POST_SIGNUP_REFERRAL_ELIGIBILITY_WINDOW_MS = 72 * 60 * 60 * 1000;
const CARLOS_REFERRAL_CODE = 'carlos';

function waitForLoggedInUser(userId: string): Promise<UserVo | undefined> {
    const initial = useAuthStore.getState();
    if (initial.user?.user_id === userId && initial.user.email) {
        return Promise.resolve(initial.user);
    }

    return new Promise((resolve) => {
        let unsubscribe = noop;
        const timeoutId = setTimeout(() => {
            unsubscribe();
            resolve(undefined);
        }, LOGGED_IN_EMAIL_WAIT_TIMEOUT_MS);
        const finish = (user: UserVo | undefined): void => {
            clearTimeout(timeoutId);
            unsubscribe();
            resolve(user);
        };
        unsubscribe = useAuthStore.subscribe((state) => {
            if (state.userId !== userId) {
                finish(undefined);
                return;
            }
            if (state.user?.user_id === userId && state.user.email) {
                finish(state.user);
            }
        });
    });
}

function isWithinPostSignupReferralEligibilityWindow(createdTs: string): boolean {
    const createdAtMs = Date.parse(createdTs);
    if (!Number.isFinite(createdAtMs)) {
        return true;
    }

    return Date.now() - createdAtMs <= POST_SIGNUP_REFERRAL_ELIGIBILITY_WINDOW_MS;
}

async function loadWasReferred(userId: string): Promise<boolean | undefined> {
    try {
        // staleTime 0 forces a fresh fetch — a cached pre-login answer could wrongly skip submission.
        const summary = await getQueryClient().fetchQuery({ ...referralsSummaryOptions(userId), staleTime: 0 });
        return getWasReferred(summary);
    } catch {
        return undefined;
    }
}

export async function submitStoredReferralAfterLogin(
    userId: string,
    getFallbackCountryCode: () => string,
): Promise<void> {
    const referralCode = readDurableReferral();
    if (!referralCode) {
        return;
    }

    const wasReferredPromise = loadWasReferred(userId);
    const user = await waitForLoggedInUser(userId);
    if (!user) {
        return;
    }

    if (
        referralCode.toLowerCase() === CARLOS_REFERRAL_CODE &&
        !isWithinPostSignupReferralEligibilityWindow(user.created_ts)
    ) {
        clearReferral();
        return;
    }

    const country = user.country || getFallbackCountryCode();
    if (!REFERRALS_ALLOWLISTED_COUNTRY_CODES.includes(country)) {
        return;
    }

    const wasReferred = await wasReferredPromise;
    if (wasReferred === undefined) {
        return;
    }
    if (wasReferred) {
        clearReferral();
        return;
    }

    await submitPostSignupReferralCode(
        user.email,
        referralCode,
        () => {
            useSnackbarStore.getState().actions.pushMessage({
                text: 'Referral code applied',
                variant: 'positive',
            });
            clearReferral();
        },
        (err) => {
            useSnackbarStore.getState().actions.pushMessage({
                text: getErrorMessage(err),
                variant: 'negative',
            });
            clearReferral();
        },
    );
}
